// The Danger Scale, draft v0.1: six levels of what an agent may do, the default policy at each,
// and common actions classed, searchable. A draft for the community to argue with.
import { MagnifyingGlass } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { Link } from "react-router";

import { CLASSED_ACTIONS, DANGER_SCALE } from "../../tools/danger";
import { useTitle } from "../../ui/hooks";
import { PageHero } from "../../ui/Section";

export function DangerScale() {
  useTitle("The Danger Scale");
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const actions = CLASSED_ACTIONS.filter((a) => !query || `${a.tool} ${a.action}`.toLowerCase().includes(query));

  return (
    <>
      <PageHero kicker="§ Free tools · Draft v0.1" title={<>Danger has a scale.</>}>
        Reading a public web page and wiring money are not the same kind of act. Six levels say which agent actions may happen alone, which the owner hears about, which need a person to
        say yes, and which an agent must never do.
      </PageHero>
      <section className="wrap py-12">
        <ol className="grid gap-3">
          {DANGER_SCALE.map((l) => (
            <li key={l.level} className="card grid gap-4 p-5 sm:grid-cols-[6rem_1.1fr_1.4fr_1fr] sm:items-center sm:p-6">
              <span className={clsx("inline-flex h-14 w-14 items-center justify-center rounded-2xl border font-display text-2xl font-extrabold", l.tone)}>{l.level}</span>
              <div>
                <p className="font-display text-xl font-bold tracking-tight">{l.name}</p>
                <p className="text-[14px] text-ink2">{l.summary}</p>
              </div>
              <p className="text-[14px] text-ink2">{l.examples.join(" · ")}</p>
              <p className="font-mono text-[12.5px] font-semibold uppercase tracking-wide text-ink">{l.policy}</p>
            </li>
          ))}
        </ol>
      </section>
      <section className="wrap grid gap-10 py-6 lg:grid-cols-[1fr_1.3fr]">
        <div>
          <h2 className="h2">Common actions, classed.</h2>
          <p className="lede mt-4">The same tool can sit on several levels: reading a calendar is a 1, inviting a colleague a 3. It is the action that is classed, not the app.</p>
          <div className="card mt-8 p-6">
            <p className="font-semibold">Disagree? Good.</p>
            <p className="mt-1 text-[15px] text-ink2">
              The scale is only worth something if it is kept by people who do not sell agents, including us. Propose a change, or a new action, through the community.
            </p>
            <Link to="/community" className="btn-line mt-4">
              Propose a change
            </Link>
          </div>
        </div>
        <div>
          <div className="relative">
            <MagnifyingGlass className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink3" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search an action, e.g. email" aria-label="Search actions" className="field !rounded-full ps-10" />
          </div>
          <ul className="card mt-4 divide-y divide-line">
            {actions.map((a) => {
              const l = DANGER_SCALE[a.level]!;
              return (
                <li key={`${a.tool}-${a.action}`} className="flex items-center gap-3 px-5 py-3">
                  <span className={clsx("inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border font-mono text-[12px] font-bold", l.tone)}>{a.level}</span>
                  <span className="flex-1">
                    <span className="font-mono text-[13px] font-semibold">{a.tool}</span> <span className="text-ink2">{a.action}</span>
                  </span>
                  <span className="hidden text-[12.5px] text-ink3 sm:block">{l.name}</span>
                </li>
              );
            })}
            {actions.length === 0 ? <li className="px-5 py-6 text-ink3">No classed action matches. Propose it.</li> : null}
          </ul>
        </div>
      </section>
    </>
  );
}
